import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  addMonths,
  endOfMonth,
  format,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import { normalizeLanguage } from '../../constants/Languages';
import { useToast } from '../../context/ToastContext';
import TeamMemberService from '../../services/teamMemberService';
import { BRAND } from '../../theme/brandTokens';

const DATE_ADAPTER_LOCALES = { pt: ptBR, en: enUS, es };
const TIME_OFF_TYPES = ['AnnualLeave', 'SickLeave', 'Other'];
const DEFAULT_FROM_TIME = '09:00';
const DEFAULT_TO_TIME = '17:00';

const buildDefaultDateRange = () => {
  const today = new Date();
  return {
    from: format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd'),
    to: format(endOfMonth(addMonths(today, 6)), 'yyyy-MM-dd'),
  };
};

const buildInitialForm = () => ({
  type: 'AnnualLeave',
  date: null,
  isFullDay: true,
  from: DEFAULT_FROM_TIME,
  to: DEFAULT_TO_TIME,
  details: '',
});

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 10) {
      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push(value);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const buildTextFieldSx = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
});

const buildSelectFieldSx = (hasError = false) => ({
  height: '48px !important',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: hasError ? '#f44336' : BRAND.line,
    borderWidth: '2px !important',
    borderRadius: '8px !important',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${BRAND.accent} !important`,
    borderWidth: '2px !important',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '16px',
  },
});

const TeamMemberTimeOffDialog = ({
  open,
  onClose,
  teamMember,
  storeId,
  embedded = false,
}) => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToast();
  const adapterLocale = DATE_ADAPTER_LOCALES[normalizeLanguage(i18n.language)] ?? enUS;
  const dateLocale = adapterLocale;

  const [timeOffs, setTimeOffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [formData, setFormData] = useState(buildInitialForm);
  const [errors, setErrors] = useState({});
  const [timeOffToDelete, setTimeOffToDelete] = useState(null);

  const dateRange = useMemo(() => buildDefaultDateRange(), []);

  const loadTimeOffs = useCallback(async () => {
    if (!teamMember?.id || !storeId) {
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);
      const data = await TeamMemberService.getTeamMemberTimeOffs(teamMember.id, storeId, dateRange);
      setTimeOffs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load time off entries:', error);
      setLoadError(t('teamMembers.timeOff.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [teamMember?.id, storeId, dateRange, t]);

  useEffect(() => {
    if (!open) {
      setFormData(buildInitialForm());
      setErrors({});
      setLoadError(null);
      setTimeOffToDelete(null);
      return;
    }

    loadTimeOffs();
  }, [open, loadTimeOffs]);

  const formatEntryDate = (dateValue) => {
    try {
      return format(parseISO(dateValue), 'PPP', { locale: dateLocale });
    } catch {
      return dateValue;
    }
  };

  const formatEntryPeriod = (period) => {
    if (!period?.from || !period?.to) {
      return t('teamMembers.timeOff.fullDay');
    }

    return `${period.from.slice(0, 5)} - ${period.to.slice(0, 5)}`;
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.date) {
      nextErrors.date = 'teamMembers.timeOff.validation.dateRequired';
    } else if (isBefore(startOfDay(formData.date), startOfDay(new Date()))) {
      nextErrors.date = 'teamMembers.timeOff.validation.pastDate';
    }

    if (!formData.isFullDay && formData.from >= formData.to) {
      nextErrors.period = 'teamMembers.timeOff.validation.invalidPeriod';
    }

    if (!formData.isFullDay && formData.date && !nextErrors.date) {
      const selectedDay = startOfDay(formData.date);
      const today = startOfDay(new Date());
      if (selectedDay.getTime() === today.getTime()) {
        const [fromHour, fromMinute] = formData.from.split(':').map(Number);
        const now = new Date();
        const fromMinutes = (fromHour * 60) + fromMinute;
        const nowMinutes = (now.getHours() * 60) + now.getMinutes();
        if (fromMinutes < nowMinutes) {
          nextErrors.period = 'teamMembers.timeOff.validation.pastPeriod';
        }
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm() || !teamMember?.id || !storeId) {
      return;
    }

    try {
      setSaving(true);
      await TeamMemberService.createTeamMemberTimeOff(teamMember.id, storeId, {
        type: formData.type,
        date: format(formData.date, 'yyyy-MM-dd'),
        period: formData.isFullDay ? null : { from: formData.from, to: formData.to },
        details: formData.details.trim() || null,
      });
      showSuccess(t('teamMembers.timeOff.success.created'));
      setFormData(buildInitialForm());
      setErrors({});
      await loadTimeOffs();
    } catch (error) {
      console.error('Failed to create time off entry:', error);
      const errorCode = error?.response?.data?.code;
      if (errorCode === 'TimeOffConflict') {
        showError(t('teamMembers.timeOff.error.conflict'));
      } else if (errorCode === 'InvalidDateRange') {
        showError(t('teamMembers.timeOff.error.pastDate'));
      } else {
        showError(t('teamMembers.timeOff.error.create'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!timeOffToDelete || !teamMember?.id || !storeId) {
      return;
    }

    try {
      setDeletingId(timeOffToDelete.id);
      await TeamMemberService.deleteTeamMemberTimeOff(teamMember.id, timeOffToDelete.id, storeId);
      showSuccess(t('teamMembers.timeOff.success.deleted'));
      setTimeOffToDelete(null);
      await loadTimeOffs();
    } catch (error) {
      console.error('Failed to delete time off entry:', error);
      showError(t('teamMembers.timeOff.error.delete'));
    } finally {
      setDeletingId(null);
    }
  };

  const isBusy = saving || Boolean(deletingId);

  const body = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              {t('teamMembers.timeOff.scheduledTitle')}
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color: BRAND.accentText }} />
              </Box>
            ) : loadError ? (
              <Alert severity="error">{loadError}</Alert>
            ) : timeOffs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('teamMembers.timeOff.empty')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {timeOffs.map((entry) => (
                  <Box
                    key={entry.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.5,
                      border: `1px solid ${BRAND.line}`,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatEntryDate(entry.date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(`teamMembers.timeOff.types.${entry.type}`)} · {formatEntryPeriod(entry.period)}
                      </Typography>
                      {entry.details ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {entry.details}
                        </Typography>
                      ) : null}
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={t('teamMembers.timeOff.delete')}
                      onClick={() => setTimeOffToDelete(entry)}
                      disabled={deletingId === entry.id}
                      sx={{ color: '#f44336' }}
                    >
                      {deletingId === entry.id ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              borderTop: `1px solid ${BRAND.line}`,
              pt: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {t('teamMembers.timeOff.addTitle')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('teamMembers.timeOff.fields.date')} *
                  </Typography>
                  <SalovareDatePicker
                    value={formData.date}
                    onChange={(value) => setFormData((previous) => ({ ...previous, date: value }))}
                    disablePast
                    error={Boolean(errors.date)}
                    helperText={errors.date ? t(errors.date) : undefined}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('teamMembers.timeOff.fields.type')} *
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.type}
                      onChange={(event) => setFormData((previous) => ({ ...previous, type: event.target.value }))}
                      MenuProps={{ disableScrollLock: true }}
                      sx={buildSelectFieldSx()}
                    >
                      {TIME_OFF_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(`teamMembers.timeOff.types.${type}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <SwitchField
                  checked={formData.isFullDay}
                  onChange={(event) => setFormData((previous) => ({ ...previous, isFullDay: event.target.checked }))}
                  label={t('teamMembers.timeOff.fields.fullDay')}
                  helpText={t('teamMembers.timeOff.fields.fullDayHelp')}
                />

                {!formData.isFullDay ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('teamMembers.timeOff.fields.from')} *
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.from}
                          onChange={(event) => setFormData((previous) => ({ ...previous, from: event.target.value }))}
                          MenuProps={{ disableScrollLock: true }}
                          sx={buildSelectFieldSx(Boolean(errors.period))}
                        >
                          {TIME_OPTIONS.map((option) => (
                            <MenuItem key={`from-${option}`} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('teamMembers.timeOff.fields.to')} *
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={formData.to}
                          onChange={(event) => setFormData((previous) => ({ ...previous, to: event.target.value }))}
                          MenuProps={{ disableScrollLock: true }}
                          sx={buildSelectFieldSx(Boolean(errors.period))}
                        >
                          {TIME_OPTIONS.map((option) => (
                            <MenuItem key={`to-${option}`} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {errors.period ? (
                      <Typography variant="caption" sx={{ color: '#f44336', gridColumn: '1 / -1' }}>
                        {t(errors.period)}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('teamMembers.timeOff.fields.details')}
                  </Typography>
                  <Box
                    component="textarea"
                    value={formData.details}
                    onChange={(event) => setFormData((previous) => ({ ...previous, details: event.target.value }))}
                    placeholder={t('teamMembers.timeOff.fields.detailsPlaceholder')}
                    rows={3}
                    sx={{
                      ...buildTextFieldSx(),
                      resize: 'vertical',
                      minHeight: 96,
                    }}
                  />
                </Box>
              </Box>
          </Box>
        </Box>
  );

  if (embedded) {
    return (
      <>
        {body}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <PrimaryActionButton onClick={handleCreate} disabled={isBusy}>
            {saving ? t('teamMembers.timeOff.adding') : t('teamMembers.timeOff.add')}
          </PrimaryActionButton>
        </Box>
        <ConfirmDialog
          open={Boolean(timeOffToDelete)}
          onClose={() => setTimeOffToDelete(null)}
          onConfirm={handleDeleteConfirm}
          title={t('teamMembers.timeOff.confirmDeleteTitle')}
          message={t('teamMembers.timeOff.confirmDeleteMessage')}
          confirmText={t('teamMembers.timeOff.delete')}
        />
      </>
    );
  }

  return (
    <>
      <ScrollableDialog
        open={open}
        onClose={isBusy ? undefined : onClose}
        maxWidth="sm"
        title={t('teamMembers.timeOff.modalTitle')}
        subtitle={teamMember?.name ? t('teamMembers.timeOff.modalSubtitle', { name: teamMember.name }) : undefined}
        dividers
        showCloseButton
        titleSx={{ pb: 1, flexShrink: 0 }}
        actionsSx={{
          flexShrink: 0,
          justifyContent: 'space-between',
        }}
        actions={(
          <>
            <Button onClick={onClose} disabled={isBusy} color="inherit">
              {t('common.cancel')}
            </Button>
            <PrimaryActionButton onClick={handleCreate} disabled={isBusy}>
              {saving ? t('teamMembers.timeOff.adding') : t('teamMembers.timeOff.add')}
            </PrimaryActionButton>
          </>
        )}
      >
        {body}
      </ScrollableDialog>

      <ConfirmDialog
        open={Boolean(timeOffToDelete)}
        onClose={() => setTimeOffToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={t('teamMembers.timeOff.confirmDeleteTitle')}
        message={t('teamMembers.timeOff.confirmDeleteMessage')}
        confirmText={t('teamMembers.timeOff.delete')}
      />
    </>
  );
};

export default TeamMemberTimeOffDialog;
